/**
 * Servicio de Auditoría
 * Maneja consultas de auditoría y historial de cambios
 */
import api from './api';

/**
 * Obtener log de auditoría con filtros
 * @param {Object} filters - Filtros (entidad, accion, usuarioId, fechaDesde, fechaHasta, page, limit)
 * @returns {Promise<Object>} Log de auditoría paginado
 */
export const getAuditLog = async (filters = {}) => {
  try {
    const response = await api.get('/auditoria/audit-logs', {
      params: filters,
    });
    return response.data.data || response.data;
  } catch (error) {
    console.error('Error al obtener log de auditoría:', error);
    throw error;
  }
};

/**
 * Obtener un registro de auditoría por ID
 * @param {string} id - ID del registro de auditoría
 * @returns {Promise<Object>} Registro de auditoría
 */
export const getAuditById = async (id) => {
  try {
    const response = await api.get(`/auditoria/${id}`);
    return response.data.data || response.data;
  } catch (error) {
    console.error('Error al obtener registro de auditoría:', error);
    throw error;
  }
};

/**
 * Obtener historial de cambios de un cheque específico
 * @param {string} chequeId - ID del cheque
 * @returns {Promise<Array>} Historial de cambios del cheque
 */
export const getChequeHistory = async (chequeId) => {
  try {
    const response = await api.get(`/auditoria/checks/${chequeId}/history`);
    return response.data.data || response.data;
  } catch (error) {
    console.error('Error al obtener historial del cheque:', error);
    throw error;
  }
};

/**
 * Obtener historial de cambios de una entidad específica
 * @param {string} entidad - Tipo de entidad (CHEQUE, DEPOSITO, CLIENTE, etc.)
 * @param {string} entidadId - ID de la entidad
 * @returns {Promise<Array>} Historial de cambios
 */
export const getEntityHistory = async (entidad, entidadId) => {
  try {
    const response = await api.get(`/auditoria/${entidad}/${entidadId}`);
    return response.data.data || response.data;
  } catch (error) {
    console.error('Error al obtener historial de la entidad:', error);
    throw error;
  }
};

/**
 * Obtener acciones de auditoría por usuario
 * @param {string} usuarioId - ID del usuario
 * @param {Object} filters - Filtros adicionales (fechaDesde, fechaHasta, accion)
 * @returns {Promise<Array>} Acciones del usuario
 */
export const getAuditByUser = async (usuarioId, filters = {}) => {
  try {
    const response = await api.get(`/auditoria/usuario/${usuarioId}`, {
      params: filters,
    });
    return response.data.data || response.data;
  } catch (error) {
    console.error('Error al obtener auditoría por usuario:', error);
    throw error;
  }
};

/**
 * Obtener acciones de auditoría por entidad
 * @param {string} entidad - Tipo de entidad (CHEQUE, DEPOSITO, CLIENTE, etc.)
 * @param {Object} filters - Filtros adicionales
 * @returns {Promise<Array>} Acciones de la entidad
 */
export const getAuditByEntity = async (entidad, filters = {}) => {
  try {
    const response = await api.get(`/auditoria/entidad/${entidad}`, {
      params: filters,
    });
    return response.data.data || response.data;
  } catch (error) {
    console.error('Error al obtener auditoría por entidad:', error);
    throw error;
  }
};

/**
 * Obtener acciones de auditoría por tipo de acción
 * @param {string} accion - Tipo de acción (CREATE, UPDATE, DELETE, etc.)
 * @param {Object} filters - Filtros adicionales
 * @returns {Promise<Array>} Acciones del tipo especificado
 */
export const getAuditByAction = async (accion, filters = {}) => {
  try {
    const response = await api.get(`/auditoria/accion/${accion}`, {
      params: filters,
    });
    return response.data.data || response.data;
  } catch (error) {
    console.error('Error al obtener auditoría por acción:', error);
    throw error;
  }
};

/**
 * Obtener estadísticas de auditoría
 * @param {Object} filters - Filtros (fechaDesde, fechaHasta)
 * @returns {Promise<Object>} Estadísticas de auditoría
 */
export const getAuditStats = async (filters = {}) => {
  try {
    const response = await api.get('/auditoria/estadisticas', {
      params: filters,
    });
    return response.data.data || response.data;
  } catch (error) {
    console.error('Error al obtener estadísticas de auditoría:', error);
    throw error;
  }
};

/**
 * Comparar dos versiones de una entidad
 * @param {string} auditId1 - ID del primer registro de auditoría
 * @param {string} auditId2 - ID del segundo registro de auditoría
 * @returns {Promise<Object>} Comparación de versiones
 */
export const compareVersions = async (auditId1, auditId2) => {
  try {
    const response = await api.get('/auditoria/comparar', {
      params: { version1: auditId1, version2: auditId2 },
    });
    return response.data.data || response.data;
  } catch (error) {
    console.error('Error al comparar versiones:', error);
    throw error;
  }
};

/**
 * Exportar log de auditoría a Excel
 * @param {Object} filters - Filtros del reporte
 * @returns {Promise<Blob>} Archivo Excel
 */
export const exportAuditToExcel = async (filters = {}) => {
  try {
    const response = await api.get('/auditoria/export/excel', {
      params: filters,
      responseType: 'blob',
    });
    return response.data;
  } catch (error) {
    console.error('Error al exportar auditoría a Excel:', error);
    throw error;
  }
};

/**
 * Exportar log de auditoría a PDF
 * @param {Object} filters - Filtros del reporte
 * @returns {Promise<Blob>} Archivo PDF
 */
export const exportAuditToPDF = async (filters = {}) => {
  try {
    const response = await api.get('/auditoria/export/pdf', {
      params: filters,
      responseType: 'blob',
    });
    return response.data;
  } catch (error) {
    console.error('Error al exportar auditoría a PDF:', error);
    throw error;
  }
};

/**
 * Obtener actividad reciente del sistema
 * @param {number} limit - Número de registros a obtener
 * @returns {Promise<Array>} Actividad reciente
 */
export const getRecentActivity = async (limit = 50) => {
  try {
    const response = await api.get('/auditoria/reciente', {
      params: { limit },
    });
    return response.data.data || response.data;
  } catch (error) {
    console.error('Error al obtener actividad reciente:', error);
    throw error;
  }
};

export default {
  getAuditLog,
  getAuditById,
  getChequeHistory,
  getEntityHistory,
  getAuditByUser,
  getAuditByEntity,
  getAuditByAction,
  getAuditStats,
  compareVersions,
  exportAuditToExcel,
  exportAuditToPDF,
  getRecentActivity,
};
